import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../../contexts/ThemeContext';

interface TemporalPulseProps {
  /** Raw events with timestamps — component buckets them internally */
  events: { timestamp: string; category?: string }[];
  /** Number of days of history to show (default 30) */
  days?: number;
  /** Accent color name for the heatmap fill (default 'indigo') */
  accentColor?: string;
  /** Callback when a cell is clicked */
  onCellClick?: (date: string, hour: number | undefined, count: number) => void;
  /** Start collapsed with a summary line (default false) */
  defaultCollapsed?: boolean;
}

const ACCENT_HEX: Record<string, string> = {
  indigo: '#6366f1',
  purple: '#a855f7',
  blue: '#3b82f6',
  cyan: '#06b6d4',
  amber: '#f59e0b',
  emerald: '#10b981',
};

/** Bucket events into time-slot keys with counts */
function bucketEvents(
  events: { timestamp: string }[],
  days: number,
  zoom: 'day' | 'week',
): Record<string, number> {
  const buckets: Record<string, number> = {};
  const now = new Date();
  const cutoff = d3.timeDay.offset(now, -days);

  for (const evt of events) {
    const d = new Date(evt.timestamp);
    if (d < cutoff || isNaN(d.getTime())) continue;

    if (zoom === 'day') {
      const dateStr = d3.timeFormat('%Y-%m-%d')(d);
      const hour = d.getHours();
      const key = `${dateStr}-${hour}`;
      buckets[key] = (buckets[key] || 0) + 1;
    } else {
      // Week view: bucket by ISO week start (Monday) + day of week (Mon=0)
      const weekStart = d3.timeMonday(d);
      const weekKey = d3.timeFormat('%Y-%m-%d')(weekStart);
      const dow = (d.getDay() + 6) % 7; // Convert Sun=0 to Mon=0
      const key = `${weekKey}-${dow}`;
      buckets[key] = (buckets[key] || 0) + 1;
    }
  }

  return buckets;
}

const TemporalPulse: React.FC<TemporalPulseProps> = ({
  events,
  days = 30,
  accentColor = 'indigo',
  onCellClick,
  defaultCollapsed,
}) => {
  const { darkMode } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState<'day' | 'week'>('day');
  const [hovered, setHovered] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultCollapsed ?? false);

  // Mobile responsive: measure container after mount via ResizeObserver
  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [collapsed]);
  const isMobile = containerWidth > 0 && containerWidth < 480;
  const effectiveDays = isMobile ? 14 : days;

  // Store callback in ref to avoid re-running D3 on every parent render
  const clickRef = useRef(onCellClick);
  clickRef.current = onCellClick;

  useEffect(() => {
    if (collapsed) return; // Don't render D3 when accordion is collapsed
    if (!containerRef.current) return;
    const el = containerRef.current;
    d3.select(el).selectAll('*').remove();

    const cellSize = 12;
    const cellGap = 2;
    const step = cellSize + cellGap;
    const labelWidth = 110;
    const headerHeight = 18;
    const emptyColor = darkMode ? '#1f2937' : '#f3f4f6';
    const accent = ACCENT_HEX[accentColor] || ACCENT_HEX.indigo;

    const buckets = bucketEvents(events, effectiveDays, zoom);
    const maxVal = Math.max(1, ...Object.values(buckets));
    const colorScale = d3.scaleSequential(d3.interpolateRgb(emptyColor, accent)).domain([0, maxVal]);

    const today = new Date();
    const cols = zoom === 'day' ? 24 : 7;
    const rows = zoom === 'day' ? days : Math.ceil(days / 7);
    const width = labelWidth + cols * step + 4;
    const height = headerHeight + rows * step + 4;

    const svg = d3
      .select(el)
      .append('svg')
      .attr('width', Math.max(width, el.clientWidth))
      .attr('height', height);

    // ── Header labels (hour markers or day-of-week) ──
    if (zoom === 'day') {
      [0, 3, 6, 9, 12, 15, 18, 21].forEach(h => {
        svg
          .append('text')
          .attr('x', labelWidth + h * step + cellSize / 2)
          .attr('y', 12)
          .attr('text-anchor', 'middle')
          .attr('font-size', 9)
          .attr('fill', darkMode ? '#6b7280' : '#9ca3af')
          .text(`${h}`);
      });
    } else {
      ['M', 'T', 'W', 'T', 'F', 'S', 'S'].forEach((d, i) => {
        svg
          .append('text')
          .attr('x', labelWidth + i * step + cellSize / 2)
          .attr('y', 12)
          .attr('text-anchor', 'middle')
          .attr('font-size', 9)
          .attr('fill', darkMode ? '#6b7280' : '#9ca3af')
          .text(d);
      });
    }

    // ── Rows (most recent day at top) ──
    for (let row = 0; row < rows; row++) {
      const y = headerHeight + row * step;
      const dayOffset = zoom === 'day' ? row : row * 7;
      const date = d3.timeDay.offset(today, -dayOffset);
      const weekNum = d3.timeWeek.count(d3.timeYear(date), date);
      const dateStr = d3.timeFormat('%b %d')(date);
      const relLabel = dayOffset === 0 ? 'today' : dayOffset === 1 ? '1d ago' : `${dayOffset}d ago`;

      // Week number
      svg
        .append('text')
        .attr('x', 0)
        .attr('y', y + cellSize - 1)
        .attr('font-size', 8)
        .attr('fill', darkMode ? '#4b5563' : '#d1d5db')
        .text(`W${weekNum}`);

      // Date
      svg
        .append('text')
        .attr('x', 28)
        .attr('y', y + cellSize - 1)
        .attr('font-size', 8)
        .attr('fill', darkMode ? '#9ca3af' : '#6b7280')
        .text(dateStr);

      // Relative offset
      svg
        .append('text')
        .attr('x', 74)
        .attr('y', y + cellSize - 1)
        .attr('font-size', 7)
        .attr('fill', darkMode ? '#374151' : '#e5e7eb')
        .text(relLabel);

      // Cells
      for (let col = 0; col < cols; col++) {
        let key: string;
        let tooltipText: string;

        if (zoom === 'day') {
          key = `${d3.timeFormat('%Y-%m-%d')(date)}-${col}`;
          tooltipText = `${dateStr} ${String(col).padStart(2, '0')}:00 — ${buckets[key] || 0} events`;
        } else {
          // Use same ISO week start (Monday) key as bucketEvents
          const rowWeekStart = d3.timeMonday(date);
          key = `${d3.timeFormat('%Y-%m-%d')(rowWeekStart)}-${col}`;
          const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
          tooltipText = `Week ${weekNum}, ${dayNames[col]} — ${buckets[key] || 0} events`;
        }

        const val = buckets[key] || 0;

        svg
          .append('rect')
          .attr('x', labelWidth + col * step)
          .attr('y', y)
          .attr('width', cellSize)
          .attr('height', cellSize)
          .attr('rx', 2)
          .attr('fill', val > 0 ? colorScale(val) : emptyColor)
          .attr('cursor', onCellClick ? 'pointer' : 'default')
          .on('click', () => clickRef.current?.(d3.timeFormat('%Y-%m-%d')(date), zoom === 'day' ? col : undefined, val))
          .append('title')
          .text(tooltipText);
      }
    }
  }, [events, effectiveDays, zoom, darkMode, accentColor, collapsed]);

  const btnClass = (active: boolean) =>
    `px-2 py-0.5 text-[10px] rounded transition-colors ${
      active
        ? darkMode
          ? 'bg-indigo-600 text-white'
          : 'bg-indigo-100 text-indigo-700'
        : darkMode
          ? 'bg-gray-700 text-gray-400 hover:text-gray-300'
          : 'bg-gray-100 text-gray-500 hover:text-gray-700'
    }`;

  // Collapsed summary view
  if (collapsed) {
    const weekAgo = Date.now() - 7 * 86400000;
    const recentCount = events.filter(e => new Date(e.timestamp).getTime() > weekAgo).length;
    return (
      <div ref={containerRef}>
        <button
          onClick={() => setCollapsed(false)}
          className={`w-full flex items-center justify-between px-2 py-3 text-xs rounded transition-colors ${
            darkMode ? 'text-gray-400 hover:bg-gray-700/50' : 'text-gray-500 hover:bg-gray-100'
          }`}
        >
          <span>
            Activity: <span className="font-semibold">{recentCount}</span> event{recentCount !== 1 ? 's' : ''} in the last 7 days
          </span>
          <span className="text-[10px]">&#9662;</span>
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-1 mb-2">
        {defaultCollapsed !== undefined && (
          <button
            onClick={() => setCollapsed(true)}
            className={`px-1.5 py-0.5 rounded text-[10px] ${
              darkMode ? 'text-gray-500 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-200'
            }`}
            title="Collapse"
          >
            &#9652;
          </button>
        )}
        <button onClick={() => setZoom('day')} className={btnClass(zoom === 'day')}>
          24h
        </button>
        <button onClick={() => setZoom('week')} className={btnClass(zoom === 'week')}>
          Weekly
        </button>
      </div>
      <div
        ref={containerRef}
        className={`max-h-[260px] transition-all ${isMobile ? 'overflow-x-auto' : hovered ? 'overflow-y-auto overflow-x-auto' : 'overflow-hidden'}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      />
    </div>
  );
};

export default TemporalPulse;
